// =====================================================================
// /reset-password — destino del email "restablecer contraseña".
//
// Supabase manda al usuario aquí desde resetPasswordForEmail(). Llega
// con una sesión temporal de tipo "recovery" que permite hacer
// updateUser({ password }). Después de guardar, redirige a /portal o
// /admin según si es staff.
// =====================================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { theme } from '../styles/theme';
import { Seo } from '../lib/seo';

export function ResetPassword() {
  const { session, staff } = useAuth();
  const nav = useNavigate();

  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [recoveryReady, setRecoveryReady] = useState(false);

  // Detectar evento PASSWORD_RECOVERY que dispara Supabase cuando el usuario
  // llega aquí desde el link del email.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setRecoveryReady(true);
      }
    });
    // Si ya tiene sesión al cargar, asumimos que ya pasó por el recovery
    if (session) setRecoveryReady(true);
    return () => { sub.subscription.unsubscribe(); };
  }, [session]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (pwd.length < 8) { setErr('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (pwd !== pwd2) { setErr('Las contraseñas no coinciden.'); return; }
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password: pwd });
    setSubmitting(false);
    if (error) { setErr(error.message); return; }
    setOk(true);
    setTimeout(() => {
      nav(staff?.active ? '/admin' : '/portal', { replace: true });
    }, 1500);
  };

  return (
    <div style={{ background: theme.bg, color: theme.text, fontFamily: theme.font.sans }}>
      <Seo title="Restablecer contraseña · NULED" description="Establece una contraseña nueva para tu cuenta." />
      <Header />
      <section style={{ maxWidth: 460, margin: '60px auto', padding: 24 }}>
        <div style={{
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          padding: 32,
        }}>
          <div style={{
            fontSize: 11, letterSpacing: 2, textTransform: 'uppercase',
            color: theme.accentDark, fontWeight: 700, marginBottom: 8,
          }}>
            Restablecer contraseña
          </div>
          <h1 style={{
            fontFamily: theme.font.head, fontSize: 24, fontWeight: 400,
            color: theme.brand, margin: '0 0 24px',
          }}>
            Establece tu nueva contraseña
          </h1>

          {!recoveryReady && !session ? (
            <div style={{
              padding: 14, background: 'rgba(220,38,38,0.05)',
              border: `1px solid ${theme.err}`, color: theme.err,
              fontSize: 13, lineHeight: 1.5,
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Link inválido o vencido.</div>
              Este link es de un solo uso y vence en 1 hora.
              Vuelve a <a href="/login" style={{ color: theme.brand }}>/login</a> y
              pide otro link de restablecimiento.
            </div>
          ) : ok ? (
            <div style={{
              padding: 14, background: theme.accentSoft,
              border: `1px solid ${theme.accent}`, color: theme.brand,
              fontSize: 13, lineHeight: 1.5,
            }}>
              ✓ Contraseña establecida. Te llevamos a tu cuenta…
            </div>
          ) : (
            <form onSubmit={submit}>
              <Field label="Nueva contraseña">
                <input
                  type={show ? 'text' : 'password'}
                  value={pwd}
                  onChange={e => setPwd(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                  autoComplete="new-password"
                  required
                  style={inputStyle}
                />
              </Field>

              <div style={{ marginTop: 14 }}>
                <Field label="Confirmar contraseña">
                  <input
                    type={show ? 'text' : 'password'}
                    value={pwd2}
                    onChange={e => setPwd2(e.target.value)}
                    autoComplete="new-password"
                    required
                    style={inputStyle}
                  />
                </Field>
              </div>

              <label style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 12, color: theme.textMuted, cursor: 'pointer',
                marginTop: 12, marginBottom: 4,
              }}>
                <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} />
                Mostrar contraseña
              </label>

              {err && (
                <div style={{
                  color: theme.err, fontSize: 13, marginTop: 12, padding: 10,
                  background: 'rgba(220,38,38,0.05)', border: `1px solid ${theme.err}`,
                }}>
                  {err}
                </div>
              )}

              <button type="submit" disabled={submitting} style={{
                marginTop: 16, width: '100%',
                background: theme.accent, color: theme.brand, border: 'none',
                padding: '14px 16px', fontSize: 13, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.4,
                cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1,
                fontFamily: theme.font.sans,
              }}>
                {submitting ? 'Guardando…' : 'Establecer contraseña'}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{
        fontSize: 11, color: theme.textMuted, marginBottom: 6,
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700,
      }}>
        {label}
      </div>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', background: theme.bg, border: `1px solid ${theme.border}`,
  padding: '12px 14px', color: theme.text, fontSize: 14,
  fontFamily: theme.font.sans, outline: 'none', boxSizing: 'border-box',
};
